import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import type {
	PersistedAgentConfig,
	RunDetail,
	RunsListResponse,
	RunMessage,
} from '@/types/agent';
import type { RawInsight } from '@/types/inbox';

interface AgentDetailPanelProps {
	agent: PersistedAgentConfig;
	onBack: () => void;
}

export const AgentDetailPanel = ({ agent, onBack }: AgentDetailPanelProps) => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [runsData, setRunsData] = useState<RunsListResponse>({
		runs: [],
		has_more: false,
	});
	const [runsOffset, setRunsOffset] = useState(0);
	const [loadingRuns, setLoadingRuns] = useState(false);
	const [loading, setLoading] = useState(true);
	const [expandedSystemPrompt, setExpandedSystemPrompt] = useState(false);
	const [taskInput, setTaskInput] = useState(agent.task || '');
	const [runningAgent, setRunningAgent] = useState(false);
	const [runError, setRunError] = useState<string | null>(null);
	const [lastRunThreadId, setLastRunThreadId] = useState<string | null>(null);
	const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
	const [runDetail, setRunDetail] = useState<RunDetail | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [copiedJson, setCopiedJson] = useState(false);
	const [behaviorNotepad, setBehaviorNotepad] = useState<{
		content: string;
		updated_at: string;
	} | null>(null);
	const [loadingBehavior, setLoadingBehavior] = useState(true);
	const [agentHistory, setAgentHistory] = useState<
		{
			id: string;
			input_summary: string;
			timestamp?: string;
			created_at?: string;
		}[]
	>([]);
	const [loadingHistory, setLoadingHistory] = useState(true);
	const [coreBehavior, setCoreBehavior] = useState<string | null>(null);
	const [loadingCoreBehavior, setLoadingCoreBehavior] = useState(true);
	const [threadSearchQuery, setThreadSearchQuery] = useState('');
	const [threadSearchResults, setThreadSearchResults] = useState<
		| {
				id: string;
				input_summary?: string;
				score?: number;
				[key: string]: unknown;
		  }[]
		| null
	>(null);
	const [searchingThreads, setSearchingThreads] = useState(false);
	const [collapsedRows, setCollapsedRows] = useState<Record<number, boolean>>(
		{},
	);
	const [jsonViewRows, setJsonViewRows] = useState<Record<number, boolean>>({});
	const [rowRefs] = useState<Record<number, HTMLDivElement | null>>({});
	const [proposedInsights, setProposedInsights] = useState<RawInsight[]>([]);
	const [loadingInsights, setLoadingInsights] = useState(true);
	const [refreshingInsights, setRefreshingInsights] = useState(false);

	const fetchRuns = useCallback(
		async (offset = 0, append = false) => {
			if (!userId) return;

			try {
				setLoadingRuns(true);
				const response = await bridgewayApi
					.get<RunsListResponse>(
						`/api/user/${userId}/agents/${agent.id}/runs?offset=${offset}&limit=20`,
					)
					.catch(() => ({ runs: [], has_more: false }));

				// Ensure response has the expected structure
				const validResponse = {
					runs: Array.isArray(response?.runs) ? response.runs : [],
					has_more: response?.has_more ?? false,
				};

				if (append) {
					setRunsData((prev) => ({
						runs: [...prev.runs, ...validResponse.runs],
						has_more: validResponse.has_more,
					}));
				} else {
					setRunsData(validResponse);
					if (validResponse.runs.length > 0 && !selectedThreadId) {
						setSelectedThreadId(validResponse.runs[0].thread_id);
					}
				}
				setRunsOffset(offset);
			} catch (err) {
				logger.error('Failed to fetch runs', err);
				setRunsData({ runs: [], has_more: false });
			} finally {
				setLoadingRuns(false);
			}
		},
		[userId, agent.id, bridgewayApi, selectedThreadId],
	);

	const fetchRunDetail = useCallback(
		async (threadId: string) => {
			if (!userId) return;

			try {
				setLoadingDetail(true);
				const detail = await bridgewayApi.get<RunDetail>(
					`/api/user/${userId}/agents/${agent.id}/runs/${threadId}`,
				);
				setRunDetail(detail);
			} catch (err) {
				logger.error('Failed to fetch run detail', err);
				setRunDetail(null);
			} finally {
				setLoadingDetail(false);
			}
		},
		[userId, agent.id, bridgewayApi],
	);

	useEffect(() => {
		setTaskInput(agent.task || '');
	}, [agent.id, agent.task]);

	useEffect(() => {
		const fetchExecutionData = async () => {
			if (!userId) return;

			try {
				setLoading(true);
				await fetchRuns();
			} catch (err) {
				logger.error('Failed to fetch execution data', err);
			} finally {
				setLoading(false);
			}
		};

		fetchExecutionData();
	}, [userId, agent.id, agent.name, fetchRuns]);

	useEffect(() => {
		const fetchBehavior = async () => {
			if (!userId) return;

			try {
				setLoadingBehavior(true);
				const response = await bridgewayApi.get<{
					content: string;
					updated_at: string;
				}>(`/api/user/${userId}/agents/${agent.id}/behavior/${agent.id}`);
				setBehaviorNotepad(response);
			} catch (err) {
				if (err instanceof Error && err.message.includes('404')) {
					setBehaviorNotepad(null);
				} else {
					logger.error('Failed to fetch behavior notepad', err);
				}
			} finally {
				setLoadingBehavior(false);
			}
		};

		fetchBehavior();
	}, [userId, agent.id, bridgewayApi]);

	useEffect(() => {
		const fetchHistory = async () => {
			if (!userId) return;
			try {
				setLoadingHistory(true);
				const response = await bridgewayApi.get<
					{
						id: string;
						input_summary: string;
						timestamp?: string;
						created_at?: string;
					}[]
				>(`/api/user/${userId}/agents/${agent.id}/history`);
				setAgentHistory(Array.isArray(response) ? response : []);
			} catch {
				setAgentHistory([]);
			} finally {
				setLoadingHistory(false);
			}
		};
		fetchHistory();
	}, [userId, agent.id, bridgewayApi]);

	useEffect(() => {
		const fetchCoreBehavior = async () => {
			if (!userId) return;
			try {
				setLoadingCoreBehavior(true);
				const response = await bridgewayApi.get<{ content: string }>(
					`/api/user/${userId}/notepads/core_behavior`,
				);
				setCoreBehavior(response.content);
			} catch {
				setCoreBehavior(null);
			} finally {
				setLoadingCoreBehavior(false);
			}
		};
		fetchCoreBehavior();
	}, [userId, bridgewayApi]);

	useEffect(() => {
		if (selectedThreadId) {
			fetchRunDetail(selectedThreadId);
		}
	}, [selectedThreadId, fetchRunDetail]);

	const fetchProposedInsights = useCallback(
		async (showSpinner = false) => {
			if (!userId) return;
			try {
				if (showSpinner) setRefreshingInsights(true);
				const response = await bridgewayApi.get<RawInsight[]>(
					`/api/user/${userId}/insights/`,
				);
				const all = Array.isArray(response) ? response : [];
				const pendingForThisAgent = all.filter(
					(i) => i.agent_id === agent.id && i.status === 'pending',
				);
				pendingForThisAgent.sort(
					(a, b) =>
						new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
				);
				setProposedInsights(pendingForThisAgent);
			} catch (err) {
				logger.error('Failed to fetch proposed insights', err);
				setProposedInsights([]);
			} finally {
				setLoadingInsights(false);
				if (showSpinner) setRefreshingInsights(false);
			}
		},
		[userId, agent.id, bridgewayApi],
	);

	useEffect(() => {
		void fetchProposedInsights();
	}, [fetchProposedInsights]);

	useEffect(() => {
		const hasActiveRuns =
			runsData.runs &&
			runsData.runs.some(
				(run) => run.status === 'pending' || run.status === 'running',
			);

		if (!hasActiveRuns) return;

		const pollInterval = setInterval(() => {
			fetchRuns(runsOffset);
		}, 5000);

		return () => clearInterval(pollInterval);
	}, [runsData.runs, fetchRuns, runsOffset]);

	const handleRunAgent = async () => {
		if (!userId || !taskInput.trim()) return;

		setRunningAgent(true);
		setRunError(null);
		setLastRunThreadId(null);

		try {
			const response = await bridgewayApi.post<{
				thread_id: string;
				agent_id: string;
				status: string;
			}>(`/api/user/${userId}/agents/${agent.id}/run`, {
				task_input: taskInput,
			});

			setLastRunThreadId(response.thread_id);
			setTaskInput('');
			await fetchRuns();
		} catch (err) {
			logger.error('Failed to run agent', err);
			setRunError(err instanceof Error ? err.message : 'Failed to run agent');
		} finally {
			setRunningAgent(false);
		}
	};

	const handleLoadMore = () => {
		fetchRuns(runsOffset + 20, true);
	};

	const handleCopyJson = async () => {
		if (!runDetail?.result) return;
		try {
			await navigator.clipboard.writeText(
				JSON.stringify(runDetail.result, null, 2),
			);
			setCopiedJson(true);
			setTimeout(() => setCopiedJson(false), 2000);
		} catch (err) {
			logger.error('Failed to copy JSON', err);
		}
	};

	const handleCollapseAll = () => {
		const messages = parseMessages(runDetail?.result || null);
		const newCollapsedState: Record<number, boolean> = {};
		messages.forEach((_, idx) => {
			newCollapsedState[idx] = true;
		});
		setCollapsedRows(newCollapsedState);
	};

	const handleToggleCollapse = (idx: number) => {
		const rowElement = rowRefs[idx];
		const isCurrentlyCollapsed = collapsedRows[idx];

		setCollapsedRows((prev) => ({
			...prev,
			[idx]: !prev[idx],
		}));

		if (!isCurrentlyCollapsed && rowElement) {
			setTimeout(() => {
				rowElement.scrollIntoView({ block: 'start', behavior: 'instant' });
			}, 0);
		}
	};

	const handleToggleJsonView = (idx: number) => {
		const rowElement = rowRefs[idx];

		setJsonViewRows((prev) => ({
			...prev,
			[idx]: !prev[idx],
		}));

		if (rowElement) {
			setTimeout(() => {
				rowElement.scrollIntoView({ block: 'start', behavior: 'instant' });
			}, 0);
		}
	};

	const parseMessages = (
		result: Record<string, unknown> | null,
	): RunMessage[] => {
		if (!result || !result.messages || !Array.isArray(result.messages)) {
			return [];
		}
		return result.messages as RunMessage[];
	};

	const messages = useMemo(
		() => parseMessages(runDetail?.result ?? null),
		[runDetail?.result],
	);

	const toolCallIndex = useMemo(() => {
		const toolCallToRow = new Map<
			string,
			{ aiRowIdx: number; toolName: string; args: Record<string, unknown> }
		>();
		const toolCallResponseRow = new Map<string, number>();

		messages.forEach((message, idx) => {
			if (message.type === 'ai' && message.tool_calls) {
				message.tool_calls.forEach((tc) => {
					if (tc.id) {
						toolCallToRow.set(tc.id, {
							aiRowIdx: idx,
							toolName: tc.name,
							args: tc.args,
						});
					}
				});
			} else if (message.type === 'tool' && message.tool_call_id) {
				toolCallResponseRow.set(message.tool_call_id, idx);
			}
		});

		return { toolCallToRow, toolCallResponseRow };
	}, [messages]);

	const scrollToRow = useCallback((idx: number) => {
		const el = rowRefs[idx];
		if (el) {
			el.scrollIntoView({ block: 'start', behavior: 'smooth' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getMessageContent = (message: RunMessage): string => {
		if (message.type === 'human') {
			const content =
				typeof message.content === 'string' ? message.content : '';
			return content;
		}

		if (message.type === 'ai') {
			if (Array.isArray(message.content)) {
				const textItem = message.content.find(
					(item: unknown) =>
						typeof item === 'object' &&
						item !== null &&
						'type' in item &&
						item.type === 'text',
				) as { text?: string } | undefined;
				if (textItem?.text) {
					return textItem.text;
				}
			}
			return '';
		}

		if (message.type === 'tool') {
			const content =
				typeof message.content === 'string' ? message.content : '';
			return content;
		}

		return '';
	};

	const getReasoningItems = (message: RunMessage) => {
		if (message.type === 'ai' && Array.isArray(message.content)) {
			return message.content.filter(
				(item: unknown) =>
					typeof item === 'object' &&
					item !== null &&
					'type' in item &&
					item.type === 'reasoning',
			) as Array<{ id: string; summary: unknown[] }>;
		}
		return [];
	};

	const statusColors = {
		active: { bg: '#10b981', text: 'Active' },
		paused: { bg: '#f59e0b', text: 'Paused' },
		archived: { bg: '#6b7280', text: 'Archived' },
	};

	const statusConfig = statusColors[agent.status];

	return (
		<div
			className="animate-agent-panel-slide-in"
			style={{
				minHeight: '100vh',
				background: '#F7F7F7',
				padding: '2rem 1.5rem',
			}}
		>
			<div style={{ maxWidth: '960px', margin: '0 auto' }}>
				<button
					type="button"
					onClick={onBack}
					style={{
						padding: '0.5rem 1rem',
						marginBottom: '1.5rem',
						background: 'transparent',
						border: 'none',
						color: 'var(--color-play-accent)',
						fontSize: '0.875rem',
						fontWeight: 500,
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						gap: '0.5rem',
					}}
				>
					← Back to agents
				</button>

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<div className="mb-4 flex items-start justify-between">
						<h1
							style={{
								fontSize: '2rem',
								fontWeight: 700,
								color: 'var(--text-primary)',
								letterSpacing: '-0.02em',
							}}
						>
							{agent.name}
						</h1>
						<div className="status-pill" style={{ background: '#f3f4f6' }}>
							<div
								style={{
									width: 8,
									height: 8,
									borderRadius: '50%',
									background: statusConfig.bg,
								}}
							/>
							<span style={{ color: 'var(--text-secondary)' }}>
								{statusConfig.text}
							</span>
						</div>
					</div>

					<div
						style={{
							fontSize: '1rem',
							color: 'var(--text-secondary)',
							lineHeight: 1.6,
							marginBottom: '1.5rem',
						}}
					>
						{agent.task ? (
							<ReactMarkdown>{agent.task}</ReactMarkdown>
						) : (
							'No task instructions provided'
						)}
					</div>

					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
							gap: '1rem',
						}}
					>
						{agent.heartbeat && (
							<div>
								<div
									style={{
										fontSize: '0.75rem',
										color: 'var(--text-muted)',
										marginBottom: '0.25rem',
										textTransform: 'uppercase',
										letterSpacing: '0.05em',
									}}
								>
									Heartbeat
								</div>
								<div
									style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}
								>
									{agent.heartbeat}
								</div>
							</div>
						)}
						{agent.priority && (
							<div>
								<div
									style={{
										fontSize: '0.75rem',
										color: 'var(--text-muted)',
										marginBottom: '0.25rem',
										textTransform: 'uppercase',
										letterSpacing: '0.05em',
									}}
								>
									Priority
								</div>
								<div
									style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}
								>
									{agent.priority}
								</div>
							</div>
						)}
					</div>
				</div>

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<h2
						style={{
							fontSize: '1.25rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '1rem',
						}}
					>
						Run Agent
					</h2>
					<div style={{ marginBottom: '1rem' }}>
						<label
							htmlFor="task-input"
							style={{
								display: 'block',
								fontSize: '0.875rem',
								color: 'var(--text-secondary)',
								marginBottom: '0.5rem',
							}}
						>
							Task Instructions
						</label>
						<textarea
							id="task-input"
							value={taskInput}
							onChange={(e) => setTaskInput(e.target.value)}
							placeholder="Provide context or instructions for this cycle..."
							style={{
								width: '100%',
								minHeight: '100px',
								padding: '0.75rem',
								border: '1px solid #e5e7eb',
								borderRadius: '8px',
								fontSize: '0.875rem',
								fontFamily: 'inherit',
								resize: 'vertical',
								backgroundColor: '#ffffff',
							}}
						/>
					</div>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '1rem',
						}}
					>
						<button
							type="button"
							onClick={handleRunAgent}
							disabled={runningAgent || !taskInput.trim()}
							style={{
								padding: '0.75rem 1.5rem',
								background: runningAgent
									? '#9ca3af'
									: 'var(--color-play-accent)',
								color: '#ffffff',
								border: 'none',
								borderRadius: '8px',
								fontSize: '0.875rem',
								fontWeight: 600,
								cursor: runningAgent ? 'not-allowed' : 'pointer',
								opacity: !taskInput.trim() ? 0.5 : 1,
							}}
						>
							{runningAgent ? 'Running...' : 'Run'}
						</button>
						{lastRunThreadId && (
							<div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
								Started: {lastRunThreadId.slice(0, 8)}...
							</div>
						)}
					</div>
					{runError && (
						<div
							style={{
								marginTop: '1rem',
								padding: '0.75rem',
								background: '#fef2f2',
								border: '1px solid #fecaca',
								borderRadius: '8px',
								color: '#dc2626',
								fontSize: '0.875rem',
							}}
						>
							{runError}
						</div>
					)}
				</div>

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<h2
						style={{
							fontSize: '1.25rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '1rem',
						}}
					>
						System Prompt
					</h2>
					<div
						style={{
							background: '#f9fafb',
							border: '1px solid #e5e7eb',
							borderRadius: '8px',
							padding: '1rem',
							maxHeight: expandedSystemPrompt ? 'none' : '200px',
							overflow: 'hidden',
							position: 'relative',
						}}
					>
						<div
							style={{
								fontSize: '0.875rem',
								lineHeight: 1.6,
								color: 'var(--text-secondary)',
							}}
						>
							<ReactMarkdown>{agent.system_prompt}</ReactMarkdown>
						</div>
						{!expandedSystemPrompt && agent.system_prompt.length > 300 && (
							<div
								style={{
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									height: '60px',
									background: 'linear-gradient(transparent, #f9fafb)',
								}}
							/>
						)}
					</div>
					{agent.system_prompt.length > 300 && (
						<button
							type="button"
							onClick={() => setExpandedSystemPrompt(!expandedSystemPrompt)}
							style={{
								marginTop: '0.75rem',
								padding: '0.5rem',
								background: 'transparent',
								border: 'none',
								color: 'var(--color-play-accent)',
								fontSize: '0.875rem',
								fontWeight: 500,
								cursor: 'pointer',
							}}
						>
							{expandedSystemPrompt ? 'Show less' : 'Show more'}
						</button>
					)}
				</div>

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<h2
						style={{
							fontSize: '1.25rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '1rem',
						}}
					>
						Tools & Skills
					</h2>
					<div style={{ marginBottom: '1rem' }}>
						<div
							style={{
								fontSize: '0.875rem',
								color: 'var(--text-muted)',
								marginBottom: '0.5rem',
							}}
						>
							Tools
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
							{agent.tools.length > 0 ? (
								agent.tools.map((tool) => (
									<div
										key={tool}
										style={{
											padding: '0.375rem 0.75rem',
											background: '#eff6ff',
											border: '1px solid #dbeafe',
											borderRadius: '6px',
											fontSize: '0.75rem',
											color: '#3b82f6',
											fontWeight: 500,
										}}
									>
										{tool}
									</div>
								))
							) : (
								<div
									style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}
								>
									No tools configured
								</div>
							)}
						</div>
					</div>
					<div>
						<div
							style={{
								fontSize: '0.875rem',
								color: 'var(--text-muted)',
								marginBottom: '0.5rem',
							}}
						>
							Skills
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
							{agent.skills.length > 0 ? (
								agent.skills.map((skill) => (
									<div
										key={skill}
										style={{
											padding: '0.375rem 0.75rem',
											background: '#f0fdf4',
											border: '1px solid #bbf7d0',
											borderRadius: '6px',
											fontSize: '0.75rem',
											color: '#16a34a',
											fontWeight: 500,
										}}
									>
										{skill}
									</div>
								))
							) : (
								<div
									style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}
								>
									No skills configured
								</div>
							)}
						</div>
					</div>
				</div>

				{agent.seed_context && Object.keys(agent.seed_context).length > 0 && (
					<div
						className="glass-card"
						style={{ padding: '2rem', marginBottom: '1.5rem' }}
					>
						<h2
							style={{
								fontSize: '1.25rem',
								fontWeight: 600,
								color: 'var(--text-primary)',
								marginBottom: '0.5rem',
							}}
						>
							Seed Context
						</h2>
						<p
							style={{
								fontSize: '0.75rem',
								color: 'var(--text-muted)',
								marginTop: 0,
								marginBottom: '1rem',
							}}
						>
							Static configuration the agent was created with — e.g. the
							subreddits and watch topic for a subreddit monitor.
						</p>
						<pre
							style={{
								background: '#f9fafb',
								border: '1px solid #e5e7eb',
								borderRadius: '8px',
								padding: '1rem',
								fontSize: '0.75rem',
								fontFamily: 'ui-monospace, monospace',
								color: 'var(--text-secondary)',
								overflow: 'auto',
								margin: 0,
								maxHeight: '320px',
							}}
						>
							{JSON.stringify(agent.seed_context, null, 2)}
						</pre>
					</div>
				)}

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: '1rem',
						}}
					>
						<h2
							style={{
								fontSize: '1.25rem',
								fontWeight: 600,
								color: 'var(--text-primary)',
								margin: 0,
							}}
						>
							Proposed Insights
						</h2>
						<button
							type="button"
							onClick={() => void fetchProposedInsights(true)}
							disabled={refreshingInsights}
							style={{
								padding: '0.375rem 0.75rem',
								background: 'transparent',
								border: '1px solid #e5e7eb',
								borderRadius: '6px',
								color: refreshingInsights
									? 'var(--text-muted)'
									: 'var(--color-play-accent)',
								fontSize: '0.75rem',
								fontWeight: 500,
								cursor: refreshingInsights ? 'not-allowed' : 'pointer',
							}}
						>
							{refreshingInsights ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
					<p
						style={{
							fontSize: '0.75rem',
							color: 'var(--text-muted)',
							marginTop: 0,
							marginBottom: '1rem',
						}}
					>
						Raw observations this agent has proposed to the curator. These are
						internal scratch — the curator consolidates them into the
						user-facing insights shown in the Inbox.
					</p>
					{loadingInsights ? (
						<div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
							Loading...
						</div>
					) : proposedInsights.length === 0 ? (
						<div
							style={{
								padding: '1.5rem',
								textAlign: 'center',
								background: '#f9fafb',
								borderRadius: '8px',
							}}
						>
							<p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
								No insights pending curator review
							</p>
						</div>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
							}}
						>
							{proposedInsights.map((insight) => (
								<div
									key={insight.id}
									style={{
										background: '#f9fafb',
										border: '1px solid #e5e7eb',
										borderRadius: '8px',
										padding: '0.75rem 1rem',
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											marginBottom: '0.25rem',
											gap: '0.5rem',
										}}
									>
										<span
											style={{
												fontSize: '0.6875rem',
												fontWeight: 600,
												color: 'var(--text-secondary)',
												textTransform: 'capitalize',
											}}
										>
											{insight.type}
										</span>
										<span
											style={{
												fontSize: '0.6875rem',
												color: 'var(--text-muted)',
											}}
										>
											{new Date(insight.created_at).toLocaleString()}
										</span>
									</div>
									<p
										style={{
											fontSize: '0.875rem',
											lineHeight: 1.5,
											color: 'var(--text-secondary)',
											margin: 0,
										}}
									>
										{insight.text}
									</p>
								</div>
							))}
						</div>
					)}
				</div>

				{agent.standup_format && (
					<div
						className="glass-card"
						style={{ padding: '2rem', marginBottom: '1.5rem' }}
					>
						<h2
							style={{
								fontSize: '1.25rem',
								fontWeight: 600,
								color: 'var(--text-primary)',
								marginBottom: '1rem',
							}}
						>
							Standup Format
						</h2>
						<div
							style={{
								fontSize: '0.875rem',
								color: 'var(--text-secondary)',
								lineHeight: 1.6,
							}}
						>
							<ReactMarkdown>{agent.standup_format}</ReactMarkdown>
						</div>
					</div>
				)}

				<div
					className="glass-card"
					style={{ padding: '2rem', marginBottom: '1.5rem' }}
				>
					<h2
						style={{
							fontSize: '1.25rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '1.25rem',
						}}
					>
						Agent Memory
					</h2>

					<h3
						style={{
							fontSize: '0.9375rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '0.75rem',
						}}
					>
						Behavior Notepad
					</h3>
					{loadingBehavior ? (
						<div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
							Loading...
						</div>
					) : !behaviorNotepad ? (
						<div
							style={{
								padding: '1.5rem',
								textAlign: 'center',
								background: '#f9fafb',
								borderRadius: '8px',
								marginBottom: '1.5rem',
							}}
						>
							<p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
								No behavior notepad yet
							</p>
						</div>
					) : (
						<div style={{ marginBottom: '1.5rem' }}>
							<div
								style={{
									marginBottom: '0.5rem',
									fontSize: '0.75rem',
									color: 'var(--text-muted)',
								}}
							>
								Last updated:{' '}
								{new Date(behaviorNotepad.updated_at).toLocaleString()}
							</div>
							<div
								style={{
									background: '#f9fafb',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '1rem',
								}}
							>
								<div
									style={{
										fontSize: '0.875rem',
										lineHeight: 1.6,
										color: 'var(--text-secondary)',
									}}
								>
									<ReactMarkdown>{behaviorNotepad.content}</ReactMarkdown>
								</div>
							</div>
						</div>
					)}

					<h3
						style={{
							fontSize: '0.9375rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '0.75rem',
						}}
					>
						Cycle History
					</h3>

					<form
						style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
						onSubmit={async (e) => {
							e.preventDefault();
							if (!userId || !threadSearchQuery.trim()) return;
							try {
								setSearchingThreads(true);
								setThreadSearchResults(null);
								const results = await bridgewayApi.post<
									{
										id: string;
										input_summary?: string;
										score?: number;
										[key: string]: unknown;
									}[]
								>(`/api/user/${userId}/search/agent-threads/${agent.id}`, {
									query: threadSearchQuery.trim(),
									top_k: 5,
								});
								setThreadSearchResults(Array.isArray(results) ? results : []);
							} catch {
								setThreadSearchResults([]);
							} finally {
								setSearchingThreads(false);
							}
						}}
					>
						<input
							type="text"
							placeholder="Search past threads..."
							value={threadSearchQuery}
							onChange={(e) => {
								setThreadSearchQuery(e.target.value);
								if (!e.target.value.trim()) setThreadSearchResults(null);
							}}
							style={{
								flex: 1,
								padding: '0.5rem 0.75rem',
								fontSize: '0.875rem',
								border: '1px solid #e5e7eb',
								borderRadius: '6px',
								background: '#fff',
								color: 'var(--text-primary)',
								outline: 'none',
							}}
						/>
						<button
							type="submit"
							disabled={searchingThreads || !threadSearchQuery.trim()}
							style={{
								padding: '0.5rem 1rem',
								fontSize: '0.875rem',
								fontWeight: 500,
								background: 'var(--color-play-accent)',
								color: '#fff',
								border: 'none',
								borderRadius: '6px',
								cursor:
									searchingThreads || !threadSearchQuery.trim()
										? 'not-allowed'
										: 'pointer',
								opacity:
									searchingThreads || !threadSearchQuery.trim() ? 0.5 : 1,
							}}
						>
							{searchingThreads ? 'Searching...' : 'Search'}
						</button>
					</form>

					{threadSearchResults !== null && (
						<div
							style={{ marginBottom: '1rem' }}
							data-testid="thread-search-results"
						>
							<div
								style={{
									fontSize: '0.75rem',
									color: 'var(--text-muted)',
									marginBottom: '0.5rem',
								}}
							>
								{threadSearchResults.length === 0
									? 'No matching threads found'
									: `${threadSearchResults.length} result${threadSearchResults.length === 1 ? '' : 's'}`}
							</div>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '0.5rem',
								}}
							>
								{threadSearchResults.map((result, i) => (
									<div
										key={result.id ?? i}
										style={{
											background: '#eff6ff',
											border: '1px solid #dbeafe',
											borderRadius: '8px',
											padding: '0.75rem 1rem',
										}}
									>
										{result.score !== undefined && (
											<div
												style={{
													fontSize: '0.7rem',
													color: '#3b82f6',
													fontWeight: 500,
													marginBottom: '0.25rem',
												}}
											>
												score: {Number(result.score).toFixed(3)}
											</div>
										)}
										<div
											style={{
												fontSize: '0.875rem',
												lineHeight: 1.5,
												color: 'var(--text-secondary)',
											}}
										>
											{result.input_summary ?? JSON.stringify(result)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{loadingHistory ? (
						<div
							style={{
								color: 'var(--text-muted)',
								fontSize: '0.875rem',
								marginBottom: '1.5rem',
							}}
						>
							Loading...
						</div>
					) : agentHistory.length === 0 ? (
						<div
							style={{
								padding: '1.5rem',
								textAlign: 'center',
								background: '#f9fafb',
								borderRadius: '8px',
								marginBottom: '1.5rem',
							}}
						>
							<p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
								No cycle history yet
							</p>
						</div>
					) : (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								marginBottom: '1.5rem',
							}}
						>
							{agentHistory.map((entry) => (
								<div
									key={entry.id}
									style={{
										background: '#f9fafb',
										border: '1px solid #e5e7eb',
										borderRadius: '8px',
										padding: '0.75rem 1rem',
									}}
								>
									<div
										style={{
											fontSize: '0.75rem',
											color: 'var(--text-muted)',
											marginBottom: '0.25rem',
										}}
									>
										{new Date(
											entry.timestamp || entry.created_at || '',
										).toLocaleString()}
									</div>
									<div
										style={{
											fontSize: '0.875rem',
											lineHeight: 1.5,
											color: 'var(--text-secondary)',
										}}
									>
										{entry.input_summary}
									</div>
								</div>
							))}
						</div>
					)}

					<h3
						style={{
							fontSize: '0.9375rem',
							fontWeight: 600,
							color: 'var(--text-primary)',
							marginBottom: '0.75rem',
						}}
					>
						User Preferences
					</h3>
					{loadingCoreBehavior ? (
						<div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
							Loading...
						</div>
					) : !coreBehavior ? (
						<div
							style={{
								padding: '1.5rem',
								textAlign: 'center',
								background: '#f9fafb',
								borderRadius: '8px',
							}}
						>
							<p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
								No user preferences yet
							</p>
						</div>
					) : (
						<div
							style={{
								background: '#f9fafb',
								border: '1px solid #e5e7eb',
								borderRadius: '8px',
								padding: '1rem',
							}}
						>
							<div
								style={{
									fontSize: '0.875rem',
									lineHeight: 1.6,
									color: 'var(--text-secondary)',
								}}
							>
								<ReactMarkdown>{coreBehavior}</ReactMarkdown>
							</div>
						</div>
					)}
				</div>
			</div>

			<div
				className="glass-card"
				style={{
					padding: '2rem',
					marginBottom: '1.5rem',
					marginLeft: '1.5rem',
					marginRight: '1.5rem',
				}}
			>
				<h2
					style={{
						fontSize: '1.25rem',
						fontWeight: 600,
						color: 'var(--text-primary)',
						marginBottom: '1rem',
					}}
				>
					LangGraph Runs
				</h2>
				{loading ? (
					<div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
						Loading...
					</div>
				) : runsData.runs.length === 0 ? (
					<div
						style={{
							padding: '2rem',
							textAlign: 'center',
							background: '#f9fafb',
							borderRadius: '8px',
						}}
					>
						<p
							style={{
								fontSize: '0.875rem',
								color: 'var(--text-muted)',
							}}
						>
							No runs yet
						</p>
					</div>
				) : (
					<div style={{ display: 'flex', gap: '1.5rem' }}>
						<div
							style={{
								width: '260px',
								flexShrink: 0,
								borderRight: '1px solid #e5e7eb',
								paddingRight: '1rem',
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '0.5rem',
								}}
							>
								{runsData.runs.map((run) => {
									const isSelected = selectedThreadId === run.thread_id;
									const statusColors: Record<
										string,
										{ bg: string; text: string }
									> = {
										pending: { bg: '#fef3c7', text: '#92400e' },
										running: { bg: '#dbeafe', text: '#1e40af' },
										success: { bg: '#d1fae5', text: '#065f46' },
										error: { bg: '#fecaca', text: '#991b1b' },
										interrupted: { bg: '#e5e7eb', text: '#374151' },
									};
									const statusConfig =
										statusColors[run.status] || statusColors.pending;

									return (
										<button
											type="button"
											key={run.thread_id}
											onClick={() => setSelectedThreadId(run.thread_id)}
											style={{
												padding: '0.75rem',
												background: isSelected ? '#f3f4f6' : '#ffffff',
												border: `1px solid ${isSelected ? '#d1d5db' : '#e5e7eb'}`,
												borderRadius: '6px',
												cursor: 'pointer',
												textAlign: 'left',
												transition: 'all 0.2s',
											}}
										>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '0.5rem',
													marginBottom: '0.5rem',
												}}
											>
												<div
													style={{
														fontSize: '0.875rem',
														fontWeight: 500,
														color: 'var(--text-primary)',
													}}
												>
													{new Date(run.created_at).toLocaleString('en-US', {
														month: 'short',
														day: 'numeric',
														hour: 'numeric',
														minute: '2-digit',
													})}
												</div>
												<div
													className="status-pill"
													style={{
														background: statusConfig.bg,
														padding: '0.125rem 0.375rem',
													}}
												>
													<span
														style={{
															fontSize: '0.625rem',
															color: statusConfig.text,
														}}
													>
														{run.status}
													</span>
												</div>
											</div>
											<div
												style={{
													fontSize: '0.625rem',
													color: 'var(--text-muted)',
													fontFamily: 'ui-monospace, monospace',
													wordBreak: 'break-all',
												}}
											>
												{run.thread_id}
											</div>
											{run.task_input && (
												<div
													style={{
														fontSize: '0.75rem',
														color: 'var(--text-secondary)',
														marginTop: '0.25rem',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{run.task_input.slice(0, 40)}
													{run.task_input.length > 40 ? '...' : ''}
												</div>
											)}
										</button>
									);
								})}
							</div>
							{runsData.has_more && (
								<button
									type="button"
									onClick={handleLoadMore}
									disabled={loadingRuns}
									style={{
										marginTop: '0.75rem',
										width: '100%',
										padding: '0.5rem',
										background: 'transparent',
										border: '1px solid #e5e7eb',
										borderRadius: '6px',
										color: 'var(--color-play-accent)',
										fontSize: '0.875rem',
										fontWeight: 500,
										cursor: loadingRuns ? 'not-allowed' : 'pointer',
										opacity: loadingRuns ? 0.5 : 1,
									}}
								>
									{loadingRuns ? 'Loading...' : 'Load more'}
								</button>
							)}
						</div>

						<div style={{ flex: 1, minWidth: 0 }}>
							{loadingDetail ? (
								<div
									style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}
								>
									Loading details...
								</div>
							) : !runDetail ? (
								<div
									style={{
										padding: '2rem',
										textAlign: 'center',
										background: '#f9fafb',
										borderRadius: '8px',
									}}
								>
									<p
										style={{
											fontSize: '0.875rem',
											color: 'var(--text-muted)',
										}}
									>
										Select a run to view details
									</p>
								</div>
							) : (
								<div>
									<div
										style={{
											marginBottom: '1.5rem',
											padding: '1rem',
											background: '#f9fafb',
											borderRadius: '8px',
										}}
									>
										<div
											style={{
												display: 'grid',
												gridTemplateColumns: 'auto 1fr',
												gap: '0.5rem 1rem',
												fontSize: '0.75rem',
												marginBottom: '0.75rem',
											}}
										>
											<div style={{ color: 'var(--text-muted)' }}>
												Thread ID:
											</div>
											<div
												style={{
													color: 'var(--text-primary)',
													fontFamily: 'ui-monospace, monospace',
													wordBreak: 'break-all',
												}}
											>
												{runDetail.thread_id}
											</div>
											<div style={{ color: 'var(--text-muted)' }}>Run ID:</div>
											<div
												style={{
													color: 'var(--text-primary)',
													fontFamily: 'ui-monospace, monospace',
													wordBreak: 'break-all',
												}}
											>
												{runDetail.run_id}
											</div>
											<div style={{ color: 'var(--text-muted)' }}>Status:</div>
											<div style={{ color: 'var(--text-primary)' }}>
												{runDetail.status}
											</div>
											<div style={{ color: 'var(--text-muted)' }}>Created:</div>
											<div style={{ color: 'var(--text-primary)' }}>
												{new Date(runDetail.created_at).toLocaleString()}
											</div>
											{runDetail.completed_at && (
												<>
													<div style={{ color: 'var(--text-muted)' }}>
														Completed:
													</div>
													<div style={{ color: 'var(--text-primary)' }}>
														{new Date(runDetail.completed_at).toLocaleString()}
													</div>
												</>
											)}
										</div>
										<a
											href={runDetail.studio_url}
											target="_blank"
											rel="noopener noreferrer"
											style={{
												fontSize: '0.875rem',
												color: 'var(--color-play-accent)',
												textDecoration: 'none',
												fontWeight: 500,
												display: 'block',
											}}
										>
											Open in LangGraph Studio →
										</a>
										<div
											style={{
												display: 'flex',
												gap: '1rem',
												marginTop: '0.5rem',
											}}
										>
											<button
												type="button"
												onClick={handleCopyJson}
												style={{
													fontSize: '0.875rem',
													color: 'var(--color-play-accent)',
													fontWeight: 500,
													background: 'none',
													border: 'none',
													padding: 0,
													cursor: 'pointer',
												}}
											>
												{copiedJson ? 'Copied!' : 'Copy Run JSON'}
											</button>
											<button
												type="button"
												onClick={handleCollapseAll}
												style={{
													fontSize: '0.875rem',
													color: 'var(--color-play-accent)',
													fontWeight: 500,
													background: 'none',
													border: 'none',
													padding: 0,
													cursor: 'pointer',
												}}
											>
												Collapse All
											</button>
										</div>
									</div>

									{runDetail.error && (
										<div
											style={{
												marginBottom: '1.5rem',
												padding: '0.75rem',
												background: '#fef2f2',
												border: '1px solid #fecaca',
												borderRadius: '8px',
												color: '#dc2626',
												fontSize: '0.875rem',
											}}
										>
											<strong>Error:</strong> {runDetail.error}
										</div>
									)}

									<h3
										style={{
											fontSize: '1rem',
											fontWeight: 600,
											color: 'var(--text-primary)',
											marginBottom: '1rem',
										}}
									>
										Messages
									</h3>

									{messages.length === 0 ? (
										<div
											style={{
												padding: '1rem',
												textAlign: 'center',
												background: '#f9fafb',
												borderRadius: '8px',
												fontSize: '0.875rem',
												color: 'var(--text-muted)',
											}}
										>
											No messages
										</div>
									) : (
										<div
											style={{
												border: '1px solid #e5e7eb',
												borderRadius: '8px',
												overflow: 'hidden',
											}}
										>
											{/* Header Row */}
											<div
												style={{
													display: 'flex',
													background: '#f9fafb',
													borderBottom: '1px solid #e5e7eb',
													padding: '0.75rem',
													fontSize: '0.875rem',
													fontWeight: 600,
													color: 'var(--text-muted)',
												}}
											>
												<div style={{ width: '40px', flexShrink: 0 }}>#</div>
												<div style={{ width: '80px', flexShrink: 0 }}>Role</div>
												<div style={{ width: '120px', flexShrink: 0 }}>
													Name
												</div>
												<div style={{ flex: 1 }}>Content</div>
												<div style={{ width: '140px', flexShrink: 0 }}>
													Actions
												</div>
											</div>

											{/* Scrollable Messages Container */}
											<div
												style={{
													maxHeight: '70vh',
													overflowY: 'auto',
												}}
											>
												{messages.map((message, idx) => {
													const roleBadgeColors: Record<
														string,
														{ bg: string; text: string }
													> = {
														human: { bg: '#dbeafe', text: '#1e40af' },
														ai: { bg: '#f3e8ff', text: '#6b21a8' },
														tool: { bg: '#d1fae5', text: '#065f46' },
													};
													const roleConfig =
														roleBadgeColors[message.type] ||
														roleBadgeColors.human;
													const isCollapsed = collapsedRows[idx];
													const isJsonView = jsonViewRows[idx];
													const reasoningItems = getReasoningItems(message);
													const toolOrigin =
														message.type === 'tool' && message.tool_call_id
															? toolCallIndex.toolCallToRow.get(
																	message.tool_call_id,
																)
															: undefined;

													return (
														<div
															key={idx}
															// Block body: React 19 treats a ref callback's return value as a cleanup
															// function, so the assignment expression's value must not leak out.
															ref={(el) => {
																rowRefs[idx] = el;
															}}
															style={{
																borderBottom:
																	idx < messages.length - 1
																		? '1px solid #e5e7eb'
																		: 'none',
															}}
														>
															{/* Sticky Header Bar */}
															<div
																style={{
																	display: 'flex',
																	alignItems: 'center',
																	position: 'sticky',
																	top: 0,
																	zIndex: 10,
																	background: '#fff',
																	borderBottom: '1px solid #e5e7eb',
																	padding: '0.75rem',
																	fontSize: '0.875rem',
																}}
															>
																<div
																	style={{
																		width: '40px',
																		flexShrink: 0,
																		color: 'var(--text-muted)',
																	}}
																>
																	{idx + 1}
																</div>
																<div style={{ width: '80px', flexShrink: 0 }}>
																	<div
																		className="status-pill"
																		style={{
																			background: roleConfig.bg,
																			padding: '0.25rem 0.5rem',
																			display: 'inline-block',
																		}}
																	>
																		<span
																			style={{
																				fontSize: '0.75rem',
																				color: roleConfig.text,
																				fontWeight: 500,
																			}}
																		>
																			{message.type}
																		</span>
																	</div>
																</div>
																<div
																	style={{
																		width: '120px',
																		flexShrink: 0,
																		color: 'var(--text-secondary)',
																	}}
																>
																	{message.type === 'tool'
																		? message.name || '--'
																		: ''}
																</div>
																<div
																	style={{
																		flex: 1,
																		fontSize: '0.75rem',
																		color: 'var(--text-secondary)',
																	}}
																>
																	{toolOrigin && (
																		<>
																			<button
																				type="button"
																				onClick={() =>
																					scrollToRow(toolOrigin.aiRowIdx)
																				}
																				style={{
																					background: 'none',
																					border: 'none',
																					color: 'var(--color-play-accent)',
																					cursor: 'pointer',
																					textDecoration: 'underline',
																					padding: 0,
																					fontSize: '0.75rem',
																					fontWeight: 500,
																				}}
																			>
																				Called from row{' '}
																				{toolOrigin.aiRowIdx + 1}
																			</button>
																			{' | '}
																			<strong>{toolOrigin.toolName}</strong>(
																			{JSON.stringify(toolOrigin.args)})
																		</>
																	)}
																</div>
																<div
																	style={{
																		width: '140px',
																		flexShrink: 0,
																		display: 'flex',
																		gap: '0.5rem',
																	}}
																>
																	<button
																		type="button"
																		onClick={() => handleToggleCollapse(idx)}
																		style={{
																			padding: '0.25rem 0.5rem',
																			background: 'transparent',
																			border: '1px solid #e5e7eb',
																			borderRadius: '4px',
																			color: 'var(--text-secondary)',
																			fontSize: '0.75rem',
																			cursor: 'pointer',
																			fontWeight: 500,
																		}}
																	>
																		{isCollapsed ? 'Expand' : 'Collapse'}
																	</button>
																	<button
																		type="button"
																		onClick={() => handleToggleJsonView(idx)}
																		style={{
																			padding: '0.25rem 0.5rem',
																			background: 'transparent',
																			border: '1px solid #e5e7eb',
																			borderRadius: '4px',
																			color: 'var(--text-secondary)',
																			fontSize: '0.75rem',
																			cursor: 'pointer',
																			fontWeight: 500,
																		}}
																	>
																		{isJsonView ? 'Text' : 'JSON'}
																	</button>
																</div>
															</div>

															{/* Content Area */}
															{!isCollapsed && (
																<div
																	style={{
																		padding: '0.75rem',
																		paddingLeft:
																			'calc(40px + 80px + 120px + 2.25rem)',
																		color: 'var(--text-secondary)',
																		wordBreak: 'break-word',
																	}}
																>
																	{isJsonView ? (
																		<pre
																			style={{
																				background: '#f9fafb',
																				padding: '0.75rem',
																				borderRadius: '6px',
																				fontSize: '0.75rem',
																				fontFamily: 'ui-monospace, monospace',
																				overflow: 'auto',
																				margin: 0,
																			}}
																		>
																			{JSON.stringify(message, null, 2)}
																		</pre>
																	) : (
																		<>
																			{reasoningItems.length > 0 && (
																				<div
																					style={{
																						fontSize: '0.75rem',
																						color: 'var(--text-muted)',
																						fontStyle: 'italic',
																						marginBottom: '0.5rem',
																						padding: '0.5rem',
																						background: '#f9fafb',
																						borderRadius: '4px',
																					}}
																				>
																					Reasoning (no summary)
																				</div>
																			)}

																			{message.type === 'ai' &&
																				message.tool_calls &&
																				message.tool_calls.length > 0 && (
																					<div
																						style={{
																							marginBottom: '0.75rem',
																							border: '1px solid #e5e7eb',
																							borderRadius: '4px',
																							overflow: 'hidden',
																						}}
																					>
																						<table
																							style={{
																								width: '100%',
																								fontSize: '0.75rem',
																								borderCollapse: 'collapse',
																							}}
																						>
																							<thead>
																								<tr
																									style={{
																										background: '#f9fafb',
																										borderBottom:
																											'1px solid #e5e7eb',
																									}}
																								>
																									<th
																										style={{
																											padding: '0.5rem',
																											textAlign: 'left',
																											fontWeight: 600,
																											color:
																												'var(--text-muted)',
																										}}
																									>
																										Tool
																									</th>
																									<th
																										style={{
																											padding: '0.5rem',
																											textAlign: 'left',
																											fontWeight: 600,
																											color:
																												'var(--text-muted)',
																										}}
																									>
																										Arguments
																									</th>
																									<th
																										style={{
																											padding: '0.5rem',
																											textAlign: 'left',
																											fontWeight: 600,
																											color:
																												'var(--text-muted)',
																											width: '100px',
																										}}
																									>
																										Response
																									</th>
																								</tr>
																							</thead>
																							<tbody>
																								{message.tool_calls.map(
																									(tc, tcIdx) => {
																										const responseRowIdx = tc.id
																											? toolCallIndex.toolCallResponseRow.get(
																													tc.id,
																												)
																											: undefined;
																										const argsStr =
																											JSON.stringify(tc.args);
																										const truncatedArgs =
																											argsStr.length > 80
																												? `${argsStr.substring(0, 80)}...`
																												: argsStr;

																										return (
																											<tr
																												key={tcIdx}
																												style={{
																													borderBottom:
																														tcIdx <
																														message.tool_calls!
																															.length -
																															1
																															? '1px solid #e5e7eb'
																															: 'none',
																												}}
																											>
																												<td
																													style={{
																														padding: '0.5rem',
																														fontFamily:
																															'ui-monospace, monospace',
																														color:
																															'var(--text-primary)',
																													}}
																												>
																													{tc.name}
																												</td>
																												<td
																													style={{
																														padding: '0.5rem',
																														fontFamily:
																															'ui-monospace, monospace',
																														color:
																															'var(--text-secondary)',
																														fontSize: '0.7rem',
																													}}
																												>
																													{truncatedArgs}
																												</td>
																												<td
																													style={{
																														padding: '0.5rem',
																													}}
																												>
																													{responseRowIdx !==
																													undefined ? (
																														<button
																															type="button"
																															onClick={() =>
																																scrollToRow(
																																	responseRowIdx,
																																)
																															}
																															style={{
																																background:
																																	'none',
																																border: 'none',
																																color:
																																	'var(--color-play-accent)',
																																cursor:
																																	'pointer',
																																textDecoration:
																																	'underline',
																																padding: 0,
																																fontSize:
																																	'0.7rem',
																																fontWeight: 500,
																															}}
																														>
																															→ Row{' '}
																															{responseRowIdx +
																																1}
																														</button>
																													) : (
																														<span
																															style={{
																																color:
																																	'var(--text-muted)',
																																fontSize:
																																	'0.7rem',
																															}}
																														>
																															--
																														</span>
																													)}
																												</td>
																											</tr>
																										);
																									},
																								)}
																							</tbody>
																						</table>
																					</div>
																				)}

																			<ReactMarkdown
																				components={{
																					p: ({ children }) => (
																						<p style={{ margin: 0 }}>
																							{children}
																						</p>
																					),
																					code: ({ children }) => (
																						<code
																							style={{
																								background: '#f3f4f6',
																								padding: '0.125rem 0.25rem',
																								borderRadius: '3px',
																								fontSize: '0.8em',
																							}}
																						>
																							{children}
																						</code>
																					),
																				}}
																			>
																				{getMessageContent(message)}
																			</ReactMarkdown>
																		</>
																	)}
																</div>
															)}
														</div>
													);
												})}
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
